import React, { useContext, useEffect, useState } from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  styled,
  Paper,
  Grid,
  Checkbox,
  ListItemIcon,
  Card,
  CardContent,
  Box,
  Chip,
  useTheme,
  useMediaQuery,
  Container,
  Fade,
} from '@mui/material';
import { Warehouse, Business, Person, FilterList, KeyboardArrowDown } from '@mui/icons-material';
import { get } from '../../services/apiMethods';
import { useFormik } from 'formik';
import dayjs from 'dayjs';
import FormDatePicker from '../../forms/FormDatePicker';
import FormDialogDashboard from '../../forms/FormDailogDashboard';
import WarehouseContext from '../../context/WarehouseContext';
import UserContext from '../../context/UserContext';
import ThemeModeContext from '../../context/ThemeModeContext';

export default function DashBoardPageHeaderPage({ title = '' }) {
  const { user } = useContext(UserContext);
  const { isDarkMode } = useContext(ThemeModeContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const {
    selectedWarehouse,
    setSelectedWarehouse,
    allWarehouses,
    allOwnerCodes,
    selectedOwnerCode,
    setSelectedOwnerCode,
    allOwnerGroups,
    selectedOwnerGroup,
    setSelectedOwnerGroup,
  } = useContext(WarehouseContext);
  const [open, setOpen] = useState(false);

  // useEffect(() => {
  //   if (
  //     !selectedWarehouse?.length ||
  //     !selectedOwnerCode?.length ||
  //     !selectedOwnerGroup?.length
  //   ) {
  //     alert("Please select a warehouse, owner group and owner code");
  //   }
  // }, []);

  const handleOpen = () => {
    setOpen(true);
  };

  useEffect(() => {
    if (allWarehouses.length == 1) {
      setSelectedWarehouse([allWarehouses[0]?._id]);
    }
  }, [allWarehouses]);

  useEffect(() => {
    if (allOwnerGroups.length == 1) {
      setSelectedOwnerGroup([allOwnerGroups[0]?._id]);
    }
  }, [allOwnerGroups]);

  useEffect(() => {
    if (allOwnerCodes.length == 1) {
      setSelectedOwnerCode([allOwnerCodes[0]?._id]);
    }
  }, [allOwnerCodes]);

  // Auto-select ownerGroups when warehouses are selected
  useEffect(() => {
    // Use a small delay to batch rapid warehouse selection changes
    const timer = setTimeout(() => {
      if (selectedWarehouse && selectedWarehouse.length > 0 && allOwnerGroups.length > 0) {
        // Find all ownerGroups that belong to the selected warehouses
        const relatedOwnerGroups = allOwnerGroups.filter(group =>
          selectedWarehouse.includes(group.warehouse?._id)
        );

        // Auto-select all related ownerGroups
        const ownerGroupIds = relatedOwnerGroups.map(group => group._id);

        // Only update if the selection is different
        if (JSON.stringify(ownerGroupIds.sort()) !== JSON.stringify(selectedOwnerGroup.sort())) {
          console.log('🔄 Auto-selecting ownerGroups for warehouses:', selectedWarehouse.length);
          setSelectedOwnerGroup(ownerGroupIds);
        }
      } else if (selectedWarehouse.length === 0) {
        // Clear ownerGroups when no warehouses are selected
        if (selectedOwnerGroup.length > 0) {
          console.log('🗑️ Clearing ownerGroups (no warehouses selected)');
          setSelectedOwnerGroup([]);
        }
      }
    }, 50); // Small delay to batch rapid changes

    return () => clearTimeout(timer);
  }, [selectedWarehouse, allOwnerGroups, setSelectedOwnerGroup]);

  // Auto-adjust ownerCodes when ownerGroups change
  useEffect(() => {
    // Use a small delay to batch rapid ownerGroup selection changes
    const timer = setTimeout(() => {
      if (selectedOwnerGroup && selectedOwnerGroup.length > 0 && allOwnerCodes.length > 0) {
        // Find all ownerCodes that belong to the selected ownerGroups
        const relatedOwnerCodes = allOwnerCodes.filter(code =>
          selectedOwnerGroup.includes(code.ownerGroup?._id)
        );

        // Filter out ownerCodes that no longer belong to selected ownerGroups
        const validOwnerCodes = selectedOwnerCode.filter(codeId =>
          relatedOwnerCodes.some(code => code._id === codeId)
        );

        // Only update if the selection changed
        if (JSON.stringify(validOwnerCodes.sort()) !== JSON.stringify(selectedOwnerCode.sort())) {
          console.log('🔄 Adjusting ownerCodes for ownerGroups:', selectedOwnerGroup.length);
          setSelectedOwnerCode(validOwnerCodes);
        }
      } else if (selectedOwnerGroup.length === 0) {
        // Clear ownerCodes when no ownerGroups are selected
        if (selectedOwnerCode.length > 0) {
          console.log('🗑️ Clearing ownerCodes (no ownerGroups selected)');
          setSelectedOwnerCode([]);
        }
      }
    }, 50); // Small delay to batch rapid changes

    return () => clearTimeout(timer);
  }, [selectedOwnerGroup, allOwnerCodes, setSelectedOwnerCode]);

  // Clean up ownerGroups when warehouses are unselected
  useEffect(() => {
    const timer = setTimeout(() => {
      if (
        selectedWarehouse.length > 0 &&
        selectedOwnerGroup.length > 0 &&
        allOwnerGroups.length > 0
      ) {
        // Filter out ownerGroups that no longer belong to selected warehouses
        const validOwnerGroups = selectedOwnerGroup.filter(groupId => {
          const group = allOwnerGroups.find(g => g._id === groupId);
          return group && selectedWarehouse.includes(group.warehouse?._id);
        });

        // Only update if the selection changed
        if (JSON.stringify(validOwnerGroups.sort()) !== JSON.stringify(selectedOwnerGroup.sort())) {
          console.log('🧹 Cleaning up invalid ownerGroups');
          setSelectedOwnerGroup(validOwnerGroups);
        }
      }
    }, 50); // Small delay to batch rapid changes

    return () => clearTimeout(timer);
  }, [selectedWarehouse, allOwnerGroups, selectedOwnerGroup, setSelectedOwnerGroup]);

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => {
      formik.resetForm();
    }, 200);
  };

  const handleChange = event => {
    const { name, value } = event.target;
    if (name === 'warehouseName') {
      setSelectedWarehouse(value);
    } else if (name === 'onwerGroup') {
      setSelectedOwnerGroup(value);
    } else {
      setSelectedOwnerCode(value);
    }
  };

  const handleSubmit = async values => {
    try {
      const { data } = await get();
    } catch (error) {
      console.log(error);
    }
  };

  const formik = useFormik({
    initialValues: {
      fromDate: dayjs(new Date()),
      toDate: dayjs(new Date()),
    },
    onSubmit: handleSubmit,
  });

  // Helper function to render selection chips
  const renderSelectionChips = (selectedItems, allItems, getLabel, maxDisplay = 2) => {
    if (!selectedItems?.length) return null;

    const displayItems = selectedItems.slice(0, maxDisplay);
    const remainingCount = selectedItems.length - maxDisplay;

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
        {displayItems.map(id => {
          const item = allItems.find(item => item._id === id);
          return item ? (
            <Chip
              key={id}
              label={getLabel(item)}
              size="small"
              variant="filled"
              color="primary"
              sx={{
                fontSize: '0.7rem',
                height: 20,
                bgcolor: isDarkMode ? 'rgba(25, 104, 56, 0.3)' : 'rgba(25, 104, 56, 0.1)',
                color: theme.palette.primary.main,
                '& .MuiChip-deleteIcon': {
                  fontSize: 14,
                },
              }}
            />
          ) : null;
        })}
        {remainingCount > 0 && (
          <Chip
            label={`+${remainingCount} more`}
            size="small"
            variant="outlined"
            sx={{
              fontSize: '0.7rem',
              height: 20,
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
            }}
          />
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ mb: { xs: 2, sm: 3 } }}>
      <Fade in timeout={400}>
        <Card
          elevation={0}
          sx={{
            border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
            borderRadius: { xs: 2, sm: 3 },
            background: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.7)',
            backdropFilter: 'blur(12px)',
            transition: 'all 0.2s ease',
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            {/* Compact Header Section */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: { xs: 2, sm: 3 },
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: { xs: 32, sm: 36 },
                  height: { xs: 32, sm: 36 },
                  borderRadius: 1.5,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <FilterList sx={{ fontSize: { xs: 18, sm: 20 }, color: 'white' }} />
              </Box>
              <Box>
                <Typography
                  variant={isMobile ? 'h6' : 'h5'}
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                    fontSize: { xs: '1.1rem', sm: '1.25rem' },
                    lineHeight: 1.2,
                  }}
                >
                  Filters
                </Typography>
              </Box>
            </Box>

            {/* Compact Filter Controls */}
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {/* Warehouse Selection */}
              <Grid item xs={12} md={4}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Warehouse
                      sx={{
                        fontSize: { xs: 16, sm: 18 },
                        color: theme.palette.primary.main,
                        mr: 1,
                      }}
                    />
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        fontSize: { xs: '0.875rem', sm: '0.9rem' },
                        textTransform: 'uppercase',
                      }}
                    >
                      WAREHOUSES
                    </Typography>
                  </Box>
                  <FormControl
                    fullWidth
                    size="small"
                    sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                  >
                    <InputLabel
                      id="warehouse-select-label"
                      sx={{
                        color: isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
                        '&.Mui-focused': {
                          color: theme.palette.primary.main,
                        },
                      }}
                    >
                      Select Warehouses
                    </InputLabel>
                    <Select
                      labelId="warehouse-select-label"
                      value={selectedWarehouse}
                      name="warehouseName"
                      size="small"
                      disabled={allWarehouses?.length === 1}
                      onChange={handleChange}
                      multiple
                      renderValue={selected => {
                        if (!selected?.length) return 'Select Warehouses';
                        const selectedNames = selected
                          .map(
                            value => allWarehouses.find(warehouse => warehouse._id === value)?.code
                          )
                          .filter(Boolean);
                        return selectedNames.length > 2
                          ? `${selectedNames.slice(0, 2).join(', ')} +${
                              selectedNames.length - 2
                            } more`
                          : selectedNames.join(', ');
                      }}
                      IconComponent={props => <KeyboardArrowDown {...props} />}
                      label="Select Warehouses"
                      sx={{
                        borderRadius: 1.5,
                        minHeight: '36px',
                        fontSize: { xs: '0.8rem', sm: '0.85rem' },
                        '& .MuiSelect-select': {
                          display: 'flex',
                          alignItems: 'center',
                          textTransform: 'uppercase',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main,
                        },
                      }}
                    >
                      {allWarehouses.map(w => (
                        <MenuItem key={w._id} value={w._id}>
                          <ListItemIcon>
                            <Checkbox
                              size="small"
                              checked={selectedWarehouse.indexOf(w._id) > -1}
                              sx={{
                                color: theme.palette.primary.main,
                                '&.Mui-checked': {
                                  color: theme.palette.primary.main,
                                },
                              }}
                            />
                          </ListItemIcon>
                          <Typography variant="body2">{w.code}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {renderSelectionChips(selectedWarehouse, allWarehouses, item => item.code)}
                </Box>
              </Grid>

              {/* Owner Group Selection */}
              <Grid item xs={12} md={4}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Business
                      sx={{
                        fontSize: { xs: 16, sm: 18 },
                        color: theme.palette.secondary.main,
                        mr: 1,
                      }}
                    />
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.secondary.main,
                        fontSize: { xs: '0.875rem', sm: '0.9rem' },
                        textTransform: 'uppercase',
                      }}
                    >
                      CUSTOMER GROUPS
                    </Typography>
                  </Box>
                  <FormControl
                    fullWidth
                    size="small"
                    sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                  >
                    <InputLabel
                      id="owner-group-select-label"
                      sx={{
                        color: isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
                        '&.Mui-focused': {
                          color: theme.palette.secondary.main,
                        },
                      }}
                    >
                      Select Customer Groups
                    </InputLabel>
                    <Select
                      labelId="owner-group-select-label"
                      value={selectedOwnerGroup}
                      name="onwerGroup"
                      size="small"
                      disabled={allOwnerGroups?.length === 1}
                      onChange={handleChange}
                      multiple
                      renderValue={selected => {
                        if (!selected?.length) return 'Select Customer Groups';
                        const selectedNames = selected
                          .map(value => allOwnerGroups.find(group => group._id === value)?.name)
                          .filter(Boolean);
                        return selectedNames.length > 2
                          ? `${selectedNames.slice(0, 2).join(', ')} +${
                              selectedNames.length - 2
                            } more`
                          : selectedNames.join(', ');
                      }}
                      IconComponent={props => <KeyboardArrowDown {...props} />}
                      label="Select Customer Groups"
                      sx={{
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.secondary.main,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.secondary.main,
                        },
                      }}
                    >
                      {allOwnerGroups?.map(w => (
                        <MenuItem key={w._id} value={w._id}>
                          <ListItemIcon>
                            <Checkbox
                              size="small"
                              checked={selectedOwnerGroup.indexOf(w._id) > -1}
                              sx={{
                                color: theme.palette.secondary.main,
                                '&.Mui-checked': {
                                  color: theme.palette.secondary.main,
                                },
                              }}
                            />
                          </ListItemIcon>
                          <Typography variant="body2">
                            {w.name}-{w.warehouse?.code}
                          </Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {renderSelectionChips(selectedOwnerGroup, allOwnerGroups, item => item.name)}
                </Box>
              </Grid>

              {/* Owner Code Selection */}
              <Grid item xs={12} md={4}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Person
                      sx={{
                        fontSize: { xs: 16, sm: 18 },
                        color: '#ff6b6b',
                        mr: 1,
                      }}
                    />
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: '#ff6b6b',
                        fontSize: { xs: '0.875rem', sm: '0.9rem' },
                        textTransform: 'uppercase',
                      }}
                    >
                      CUSTOMERS
                    </Typography>
                  </Box>
                  <FormControl
                    fullWidth
                    size="small"
                    sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                  >
                    <InputLabel
                      id="owner-code-select-label"
                      sx={{
                        color: isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
                        '&.Mui-focused': {
                          color: '#ff6b6b',
                        },
                      }}
                    >
                      Select Customers
                    </InputLabel>
                    <Select
                      labelId="owner-code-select-label"
                      value={selectedOwnerCode}
                      name="customerName"
                      size="small"
                      disabled={allOwnerCodes?.length === 1}
                      onChange={handleChange}
                      multiple
                      renderValue={selected => {
                        if (!selected?.length) return 'Select Customers';
                        const selectedNames = selected
                          .map(value => allOwnerCodes.find(code => code._id === value)?.name)
                          .filter(Boolean);
                        return selectedNames.length > 2
                          ? `${selectedNames.slice(0, 2).join(', ')} +${
                              selectedNames.length - 2
                            } more`
                          : selectedNames.join(', ');
                      }}
                      IconComponent={props => <KeyboardArrowDown {...props} />}
                      label="Select Customers"
                      sx={{
                        borderRadius: 1.5,
                        minHeight: '36px',
                        fontSize: { xs: '0.8rem', sm: '0.85rem' },
                        '& .MuiSelect-select': {
                          display: 'flex',
                          alignItems: 'center',
                          textTransform: 'uppercase',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#ff6b6b',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#ff6b6b',
                        },
                      }}
                    >
                      {allOwnerCodes?.map(w => (
                        <MenuItem key={w._id} value={w._id}>
                          <ListItemIcon>
                            <Checkbox
                              size="small"
                              checked={selectedOwnerCode.indexOf(w._id) > -1}
                              sx={{
                                color: '#ff6b6b',
                                '&.Mui-checked': {
                                  color: '#ff6b6b',
                                },
                              }}
                            />
                          </ListItemIcon>
                          <Typography variant="body2">
                            {w.name}-{w.ownerGroup?.warehouse?.code}
                          </Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {renderSelectionChips(selectedOwnerCode, allOwnerCodes, item => item.name)}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Fade>

      <FormDialogDashboard
        open={open}
        formik={formik}
        formTitle="Filter date"
        onClose={handleClose}
        submitButtonTitle="Apply"
      >
        <Grid rowSpacing={1} columnSpacing={2} container>
          <Grid xs={12} sm={6} md={6} item>
            <FormDatePicker formik={formik} label="From Date" name="fromDate" />
          </Grid>
          <Grid xs={12} sm={6} md={6} item>
            <FormDatePicker formik={formik} label="To Date" name="toDate" />
          </Grid>
        </Grid>
      </FormDialogDashboard>
    </Box>
  );
}
